import { AnyAction } from "redux";
import {
  AIRun,
  beginAIRun,
  initialAIRun,
  isRunLive,
} from "../../../components/shared/brand/aiProgress/aiRun";
import { reduceMatterAIEvent } from "./matterAIRun";

/*
  NOTHING IS PRE-DRAWN.

  This file used to open every matter run by painting five steps as Pending,
  mirroring a `STEP_SEQUENCE` constant in the AI engine by hand. That mirror
  was wrong on every single run: `_build_active_steps()` builds the real
  pipeline from the matter's AI toggles, so "Analysing Legal Sources" only
  exists when law/precedent fetching is on and "Generating Relevant Tasks"
  only when task creation is. The steps the engine skipped were never
  reported, so they sat Pending until the run ended — the tracker showed
  unfinished work on a matter that had finished perfectly.

  Steps now APPEND as the engine reports them (`merge_step` already did this
  for an unknown step). An empty `progress` means the engine has not spoken
  yet, which the panel renders as its connecting stage.
*/

interface MatterState {
  matters: any[];
  assignedMatters: any[];
  currentMatter: any | null;
  isLoadingCurrentMatter: boolean;
  prefetchedMatterData: any | null;
  prefetchedMatterTasks: any[];
  isPreparingDoc: boolean;
  isConnecting: boolean;
  matterId?: string;
  correlationId?: string;
  progress: any[];
  progressPercent: number;
  isRunning: boolean;
  isCompleted: boolean;
  /*
    The same run, in the shared AI model. `progress` above is the legacy
    `{step, status, messages[]}` list two screens still read; `run` is what
    `MatterForm` renders through `AIProgressPanel`, and it is the path that
    carries narration, engine-reported facts, per-document rows and the
    partial-success state. Both are fed from the SAME PubSub events — see
    `matterAIRun.ts`.
  */
  run: AIRun;
}

const initialState: MatterState = {
  matters: [],
  assignedMatters: [],
  currentMatter: null,
  isLoadingCurrentMatter: false,
  prefetchedMatterData: null,
  prefetchedMatterTasks: [],
  isPreparingDoc: false,
  isConnecting: false,
  matterId: undefined,
  correlationId: undefined,
  progress: [],
  progressPercent: 0,
  isRunning: false,
  isCompleted: false,
  run: initialAIRun
};

const merge_step = (arr: any[], patch: any) => {
  const idx = arr.findIndex((s: any) => s.step === patch.step);
  if (idx > -1) {
    const merged = {
      ...arr[idx],
      status: patch.status,
      messages: [...(arr[idx].messages || []), ...(patch.messages || [])],
      elapsedSeconds: patch.elapsedSeconds !== undefined ? patch.elapsedSeconds : arr[idx].elapsedSeconds,
      iconIdentifier: patch.iconIdentifier || arr[idx].iconIdentifier,
    };
    return [...arr.slice(0, idx), merged, ...arr.slice(idx + 1)];
  }
  return [...arr, patch];
};

export const matterReducer = (state = initialState, action: AnyAction): MatterState => {
  switch (action.type) {
    case 'FETCH_ALL_MATTERS':
      return { ...state, matters: action.payload };
    case 'APPEND_MATTERS':
      return { ...state, matters: [...state.matters, ...action.payload] };
    /*
      Board drag-and-drop. Writing the new stage into the list means the board
      survives a refetch-free rerender showing where the card actually is,
      rather than snapping back to the stage the last fetch returned.
    */
    case 'UPDATE_MATTER_BOARD_STAGE':
      return {
        ...state,
        matters: state.matters.map((matter: any) =>
          (matter.uuid || matter.MatterID) === action.payload.uuid
            ? { ...matter, board_stage: action.payload.stage }
            : matter
        ),
      };
    case 'FETCH_ASSIGNED_MATTERS':
      return { ...state, assignedMatters: action.payload };
    case 'PREPARING_MATTER_DOCUMENT':
      return {
        ...state,
        isPreparingDoc: action.payload.isPreparingDoc,
        progress: [],
        progressPercent: 0,
        isCompleted: false,
        run: initialAIRun
      };
    case 'CONNECTING_MATTER_CREATION_SERVICE':
      return {
        ...state,
        isConnecting: action.payload.isConnecting,
        matterId: action.payload.matter_id || state.matterId,
        correlationId: action.payload.correlation_id,
        isRunning: true,
        progressPercent: 0,
        isCompleted: false,
        /*
          The documents the user submitted seed the per-item rows, so they are
          on screen from the first frame instead of appearing one at a time as
          the engine happens to name each of them.
        */
        run: beginAIRun(Date.now(), action.payload.document_names || [])
      };
    case 'MATTER_CREATION_CONNECTING':
      return {
        ...state,
        isConnecting: true,
        isPreparingDoc: false,
        progressPercent: 0,
        isCompleted: false,
        run: state.run.status === 'idle' ? beginAIRun() : state.run
      };
    /*
      The raw PubSub envelope, folded into the shared model. Dispatched
      alongside the legacy step actions from the same handler — one socket,
      one set of events, two shapes of state until the last legacy screen
      moves over.
    */
    case 'MATTER_CREATION_AI_EVENT':
      return {
        ...state,
        run: reduceMatterAIEvent(state.run, action.payload)
      };
    case 'MATTER_CREATION_CONNECTED':
      return {
        ...state,
        isRunning: true,
        isConnecting: false,
        isPreparingDoc: false
      };
    case 'MATTER_CREATION_PROGRESS_INIT':
      return {
        ...state,
        isRunning: true,
        isPreparingDoc: false,
        isConnecting: false,
        progress: action.payload.steps?.length > 0 ? action.payload.steps : state.progress,
        progressPercent: 0
      };
    case 'MATTER_CREATION_STEP_STARTED':
      return {
        ...state,
        progress: merge_step(state.progress, {
          step: action.payload.step,
          status: "In Progress",
          elapsedSeconds: action.payload.elapsedSeconds,
          iconIdentifier: action.payload.iconIdentifier
        }),
        progressPercent: action.payload.progress_percent || state.progressPercent
      };
    case 'MATTER_CREATION_STEP_MESSAGE':
      return {
        ...state,
        progress: merge_step(state.progress, {
          step: action.payload.step,
          status: "In Progress",
          messages: [action.payload.message],
          elapsedSeconds: action.payload.elapsedSeconds,
          iconIdentifier: action.payload.iconIdentifier
        }),
        progressPercent: action.payload.progress_percent || state.progressPercent
      };
    case 'MATTER_CREATION_STEP_COMPLETED':
      return {
        ...state,
        progress: merge_step(state.progress, {
          step: action.payload.step,
          status: "Completed",
          messages: [action.payload.message],
          elapsedSeconds: action.payload.elapsedSeconds,
          iconIdentifier: action.payload.iconIdentifier
        }),
        progressPercent: action.payload.progress_percent || state.progressPercent
      };
    case 'MATTER_CREATION_STEP_FAILED':
      return {
        ...state,
        progress: merge_step(state.progress, {
          step: action.payload.step,
          status: "Failed",
          messages: [action.payload.message],
          elapsedSeconds: action.payload.elapsedSeconds,
          iconIdentifier: action.payload.iconIdentifier
        }),
        progressPercent: action.payload.progress_percent || state.progressPercent
      };
    case 'MATTER_CREATION_COMPLETE':
      return {
        ...state,
        isPreparingDoc: false,
        isConnecting: false,
        correlationId: undefined,
        progress: [],
        isRunning: false,
        isCompleted: true,
        /*
          A completion event has usually already been folded in through
          `MATTER_CREATION_AI_EVENT`, which is what knows whether some
          documents came back unreadable. Only a run that never saw one is
          closed here, and never over a partial success.
        */
        run: state.run.status === 'completed_with_errors'
          ? state.run
          : { ...state.run, status: 'completed', percent: 100 }
      };
    case 'MATTER_CREATION_FAILED':
      return {
        ...state,
        isPreparingDoc: false,
        isConnecting: false,
        correlationId: undefined,
        progress: [],
        isRunning: false,
        isCompleted: false,
        /*
          The panel stays on screen for a failure — it is the only place the
          engine's reason for it is shown. Only for a run that was actually
          live, though: `createMatter` dispatches this for an API error before
          any run exists, and that path already has its own toast.
        */
        run: isRunLive(state.run)
          ? {
              ...state.run,
              status: 'failed',
              error: state.run.error || action.payload?.error || null
            }
          : state.run
      };
    case 'MATTER_CREATION_CANCELLED':
      return {
        ...state,
        isPreparingDoc: false,
        isConnecting: false,
        correlationId: undefined,
        progress: [],
        isRunning: false,
        isCompleted: false,
        run: initialAIRun
      };
    case 'SET_PREFETCHED_MATTER_DATA':
      return {
        ...state,
        prefetchedMatterData: action.payload.data,
        prefetchedMatterTasks: action.payload.tasks || [],
      };
    case 'SET_CURRENT_MATTER':
      return {
        ...state,
        currentMatter: action.payload,
      };
    case 'SET_LOADING_CURRENT_MATTER':
      return {
        ...state,
        isLoadingCurrentMatter: action.payload,
      };
    case 'CLEAR_PREFETCHED_MATTER_DATA':
      return {
        ...state,
        prefetchedMatterData: null,
        prefetchedMatterTasks: [],
      };
    case 'CLEAR_CURRENT_MATTER':
      return {
        ...state,
        currentMatter: null,
        isLoadingCurrentMatter: false,
      };
    default:
      return state;
  }
};
